/**
 * NotificationsPage — e-maili teavituste haldus.
 * - Adressaatide nimekiri (lisa/kustuta/test)
 * - Iga adressaadi tellimused: mis alarmid, mis park, kui kaua peab kestma
 * - Saadetud emailide log
 */

import { useState, useEffect, useMemo } from 'react';
import { useParks } from '../contexts/ParksContext';

const DURATIONS = [
  { sec: 0, label: 'Kohe' },
  { sec: 60, label: '1 min' },
  { sec: 300, label: '5 min' },
  { sec: 600, label: '10 min' },
  { sec: 900, label: '15 min' },
  { sec: 1800, label: '30 min' },
  { sec: 3600, label: '1 tund' },
  { sec: 7200, label: '2 tundi' },
];

export function NotificationsPage() {
  const [recipients, setRecipients] = useState([]);
  const [types, setTypes] = useState([]);
  const [log, setLog] = useState([]);
  const [activeAlarms, setActiveAlarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [expanded, setExpanded] = useState(null);  // recipient_id
  const [subs, setSubs] = useState({});  // recipient_id → subscription list
  const { data: parksData } = useParks();
  const parks = parksData?.parks || [];

  // New recipient form
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, t, l, a] = await Promise.all([
        fetch('/api/notifications/recipients').then(x => x.json()),
        fetch('/api/notifications/types').then(x => x.json()),
        fetch('/api/notifications/log?limit=50').then(x => x.json()),
        fetch('/api/notifications/active').then(x => x.json()).catch(() => ({ active: [] })),
      ]);
      setRecipients(r.recipients || []);
      setTypes(t.types || []);
      setLog(l.log || []);
      setActiveAlarms(a.active || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const flash = (msg, isError = false) => {
    if (isError) setError(msg); else setInfo(msg);
    setTimeout(() => { setError(null); setInfo(null); }, 4000);
  };

  const loadSubs = async (rid) => {
    const r = await fetch(`/api/notifications/subscriptions/${rid}`).then(x => x.json());
    setSubs(prev => ({ ...prev, [rid]: r.subscriptions || [] }));
  };

  const addRecipient = async () => {
    if (!newEmail.includes('@')) return flash('Vale e-maili formaat', true);
    const r = await fetch('/api/notifications/recipients', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: newEmail, name: newName }),
    });
    const j = await r.json();
    if (!r.ok) return flash(j.error || 'Viga', true);
    setNewEmail(''); setNewName('');
    flash('Adressaat lisatud');
    loadAll();
  };

  const toggleRecipient = async (id, enabled) => {
    await fetch(`/api/notifications/recipients/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    loadAll();
  };

  const deleteRecipient = async (id) => {
    if (!confirm('Kustuta adressaat (kaasa kõik tema tellimused)?')) return;
    await fetch(`/api/notifications/recipients/${id}`, { method: 'DELETE' });
    flash('Adressaat kustutatud');
    loadAll();
  };

  const sendTest = async (id) => {
    const r = await fetch(`/api/notifications/recipients/${id}/test`, { method: 'POST' });
    const j = await r.json();
    flash(j.sent ? `Test saadetud (messageId: ${j.messageId})` : `Viga: ${j.error}`, !j.sent);
  };

  const addSub = async (rid, alarm_type, slot, min_duration_seconds) => {
    const r = await fetch('/api/notifications/subscriptions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipient_id: rid, alarm_type, slot, min_duration_seconds }),
    });
    if (!r.ok) { const j = await r.json(); return flash(j.error || 'Tellimus juba olemas', true); }
    flash('Tellimus lisatud');
    loadSubs(rid);
  };

  const updateSub = async (id, rid, updates) => {
    await fetch(`/api/notifications/subscriptions/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    loadSubs(rid);
  };

  const deleteSub = async (id, rid) => {
    await fetch(`/api/notifications/subscriptions/${id}`, { method: 'DELETE' });
    loadSubs(rid);
  };

  const expand = (rid) => {
    if (expanded === rid) { setExpanded(null); return; }
    setExpanded(rid);
    if (!subs[rid]) loadSubs(rid);
  };

  const parkOptions = useMemo(() => [
    { value: '', label: 'Kõik pargid' },
    ...parks.filter(p => p.name).map(p => ({ value: p.slot, label: `${p.slot} — ${p.name}` })),
  ], [parks]);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1280, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>Teavitused — e-maili adressaadid</h1>

      {error && <div style={{ padding: 10, background: '#ffebee', color: '#c62828', borderRadius: 4, marginBottom: 12 }}>⚠️ {error}</div>}
      {info && <div style={{ padding: 10, background: '#e8f5e9', color: '#2e7d32', borderRadius: 4, marginBottom: 12 }}>✓ {info}</div>}

      {/* Lisa uus adressaat */}
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: 14, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Lisa uus adressaat</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
            placeholder="email@example.com"
            style={{ flex: 1, padding: '7px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 }}
          />
          <input
            type="text" value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="Nimi (valikuline)"
            style={{ flex: 1, padding: '7px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 }}
          />
          <button onClick={addRecipient}
            style={{ padding: '7px 16px', background: '#11093E', color: 'white', border: 'none', borderRadius: 4, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
            + Lisa
          </button>
        </div>
      </div>

      {/* Adressaadid */}
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: '20px 0 10px' }}>Adressaadid ({recipients.length})</h2>
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
        {recipients.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 13 }}>
            Ühtegi adressaati pole lisatud. Lisa esimene ülal.
          </div>
        )}
        {recipients.map(r => (
          <div key={r.id} style={{ borderBottom: '1px solid #eee' }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', gap: 10 }}>
              <input type="checkbox" checked={r.enabled}
                onChange={() => toggleRecipient(r.id, !r.enabled)} title="Aktiveeri/peata"/>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 13, color: r.enabled ? '#222' : '#999' }}>
                  {r.email} {r.name && <span style={{ color: '#888' }}>({r.name})</span>}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>
                  {r.subscription_count} tellimust · lisatud {r.created_at?.slice(0,10)}
                </div>
              </div>
              <button onClick={() => expand(r.id)}
                style={{ padding: '5px 12px', background: '#fff', border: '1px solid #ccc', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                {expanded === r.id ? '▾ Peida' : '▸ Tellimused'}
              </button>
              <button onClick={() => sendTest(r.id)}
                style={{ padding: '5px 12px', background: '#fff', border: '1px solid #1976d2', color: '#1976d2', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                📧 Test
              </button>
              <button onClick={() => deleteRecipient(r.id)}
                style={{ padding: '5px 12px', background: '#fff', border: '1px solid #d32f2f', color: '#d32f2f', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                Kustuta
              </button>
            </div>
            {expanded === r.id && (
              <SubscriptionPanel
                rid={r.id}
                subs={subs[r.id] || []}
                types={types}
                parkOptions={parkOptions}
                onAdd={(t, s, d) => addSub(r.id, t, s, d)}
                onUpdate={(sid, u) => updateSub(sid, r.id, u)}
                onDelete={(sid) => deleteSub(sid, r.id)}
              />
            )}
          </div>
        ))}
      </div>

      {/* Aktiivsed alarmid + kestus */}
      {activeAlarms.length > 0 && (
        <>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: '24px 0 10px' }}>Aktiivsed alarmid (kestab)</h2>
          <table style={{ width: '100%', background: '#fff', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f5f5f5' }}>
              <th style={th}>Park</th><th style={th}>Alarm</th><th style={th}>Tüüp</th><th style={thR}>Kestab (min)</th>
            </tr></thead>
            <tbody>
              {activeAlarms.map(a => (
                <tr key={a.key}><td style={td}>{a.park}</td><td style={td}>{a.alarm}</td><td style={td}>{a.type}</td>
                  <td style={tdR}>{Math.round(a.duration_seconds / 60)}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Log */}
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: '24px 0 10px' }}>Saadetud emailid (viimased 50)</h2>
      <table style={{ width: '100%', background: '#fff', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f5f5f5' }}>
          <th style={th}>Aeg</th><th style={th}>Tüüp</th><th style={th}>Slot</th>
          <th style={th}>Saajad</th><th style={th}>Subject</th><th style={th}>Olek</th>
        </tr></thead>
        <tbody>
          {log.length === 0 && <tr><td colSpan="6" style={{ padding: 20, textAlign: 'center', color: '#888' }}>Pole veel saadetud</td></tr>}
          {log.map(e => (
            <tr key={e.id}>
              <td style={td}>{e.sent_at}</td>
              <td style={td}>{e.alarm_type}</td>
              <td style={td}>{e.slot ?? '—'}</td>
              <td style={{...td, fontSize: 11, color: '#666' }}>{e.recipients}</td>
              <td style={td}>{e.subject}</td>
              <td style={{ ...td, color: e.status === 'sent' ? '#2e7d32' : '#c62828' }}>{e.status}{e.error && ` · ${e.error.slice(0,40)}`}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {loading && <div style={{ padding: 10, color: '#888' }}>Laeb…</div>}
    </div>
  );
}

function SubscriptionPanel({ rid, subs, types, parkOptions, onAdd, onUpdate, onDelete }) {
  const [newType, setNewType] = useState('trip');
  const [newSlot, setNewSlot] = useState('');
  const [newDuration, setNewDuration] = useState(0);

  return (
    <div style={{ background: '#fafafa', padding: 14, borderTop: '1px solid #eee' }}>
      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Tellimused</div>

      {/* Subs list */}
      {subs.length === 0 && <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>Pole tellimusi — selle aadressi peale ei saadeta midagi.</div>}
      {subs.map(s => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
          <input type="checkbox" checked={s.enabled} onChange={() => onUpdate(s.id, { enabled: !s.enabled })} />
          <span style={{ minWidth: 200 }}>{types.find(t => t.id === s.alarm_type)?.name || s.alarm_type}</span>
          <span style={{ minWidth: 140, color: '#666' }}>
            {s.slot != null ? `Slot ${s.slot}` : 'Kõik pargid'}
          </span>
          <select value={s.min_duration_seconds} onChange={e => onUpdate(s.id, { min_duration_seconds: parseInt(e.target.value) })}
            style={{ padding: '3px 6px', fontSize: 12, border: '1px solid #ccc', borderRadius: 3 }}>
            {DURATIONS.map(d => <option key={d.sec} value={d.sec}>{d.label}</option>)}
          </select>
          <button onClick={() => onDelete(s.id)} style={{ padding: '2px 8px', fontSize: 11, background: '#fff', border: '1px solid #d32f2f', color: '#d32f2f', borderRadius: 3, cursor: 'pointer' }}>×</button>
        </div>
      ))}

      {/* Add new sub */}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '1px dashed #ddd', alignItems: 'center' }}>
        <select value={newType} onChange={e => setNewType(e.target.value)} style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #ccc', borderRadius: 4, flex: 1 }}>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={newSlot} onChange={e => setNewSlot(e.target.value)} style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #ccc', borderRadius: 4, flex: 1 }}>
          {parkOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select value={newDuration} onChange={e => setNewDuration(parseInt(e.target.value))} style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #ccc', borderRadius: 4 }}>
          {DURATIONS.map(d => <option key={d.sec} value={d.sec}>{d.label}</option>)}
        </select>
        <button onClick={() => onAdd(newType, newSlot === '' ? null : parseInt(newSlot), newDuration)}
          style={{ padding: '5px 14px', background: '#11093E', color: 'white', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>+ Lisa</button>
      </div>
    </div>
  );
}

const th = { padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #ccc', fontWeight: 600 };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '6px 10px', borderBottom: '1px solid #f0f0f0' };
const tdR = { ...td, textAlign: 'right' };

export default NotificationsPage;
